import React, { useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import { Spinner, Separator, Text } from "@fluentui/react";
import useAxios from "axios-hooks";
import PrimaryButton from "../../../Forms/PrimaryButton";
import Toggle from "../../../Forms/Toggle";
import ErrorComponent from "../../../Util/ErrorComponent";
import SuccessComponent from "../../../Util/SuccessComponent";

const MemberEmailUnsubscribeOptions = () => {
  const { email: emailFromParams } = useParams();
  const initialRef = useRef({});

  // Fetch group suppression data with user's unsubscribed groups
  const [{ data: unsubscribe, loading: userLoading, error: userError }, refetchUser] = useAxios(
    { url: `/emails/suppression-groups/${emailFromParams}`, method: "get" },
    { manual: !emailFromParams } 
  );

  // Fetch global unsubscribe status
  const [{ data: globalStatus, loading: globalStatusLoading, error: globalStatusError }, refetchGlobalStatus] = useAxios(
    { url: `/emails/suppression-global/${emailFromParams}`, method: "get" },
    { manual: !emailFromParams } 
  );

  const [, submitForm] = useAxios({}, { manual: true });

  function mapInitialValues(data) {
    if (data.unsubscribe && data.unsubscribe.suppressions && data.globalStatus !== undefined) {
      initialRef.current = { ...data.unsubscribe, globalUnsubscribe: data.globalStatus };
      return { ...data.unsubscribe, globalUnsubscribe: data.globalStatus };
    } else {
      initialRef.current = { suppressions: [], globalUnsubscribe: false };
      return { suppressions: [], globalUnsubscribe: false };
    }
  }

  if (globalStatusLoading || userLoading) return <Spinner label="Loading email options..." />;

  if (!emailFromParams) {
    return <ErrorComponent errorMessage="No email provided." showError={true} setShowError={() => {}} />;
  }

  if (globalStatusError || userError) {
    return <ErrorComponent errorMessage="Error loading email suppression data." showError={true} setShowError={() => {}} />;
  }


  return (
    <Formik
      initialValues={mapInitialValues({ unsubscribe, globalStatus })}
      validateOnMount={true}
      enableReinitialize
      onSubmit={async (values, { resetForm, setSubmitting, setStatus }) => {
        try {
            const globalChanged = initialRef.current.globalUnsubscribe !== values.globalUnsubscribe;
            if (globalChanged) {
                if (values.globalUnsubscribe) {
                    await submitForm({
                        url: `/emails/suppression-global/${emailFromParams}`,
                        method: "post",
                        data: { unsubscribed: true },
                    });
                } else {
                    if (initialRef.current.globalUnsubscribe) {
                        await submitForm({
                            url: `/emails/suppression-global/${emailFromParams}/resubscribe`,
                            method: "delete",
                        });
                    }
                }
            }
            // --- Individual group unsubscribe and resubscribe ---
            const groupIdsToUnsubscribe = values.suppressions.filter((g) => {
                const initialVal = initialRef.current.suppressions.find(s => s.id === g.id);
                return g.suppressed && !initialVal.suppressed;
            }).map((g) => g.id) || [];

            const groupIdsToResubscribe = values.suppressions.filter((g) => {
                const initialVal = initialRef.current.suppressions.find(s => s.id === g.id);
                return !g.suppressed && initialVal.suppressed;
            }).map((g) => g.id) || [];

            if (groupIdsToUnsubscribe.length > 0) {
                await submitForm({
                    url: `/emails/suppression-groups/${emailFromParams}/unsubscribe`,
                    method: "post",
                    data: groupIdsToUnsubscribe,
                });
            }

            if (groupIdsToResubscribe.length > 0) {
                await submitForm({
                    url: `/emails/suppression-groups/${emailFromParams}/resubscribe`,
                    method: "post",
                    data: groupIdsToResubscribe,
                });
            }

            setStatus("success");
            resetForm({ values });
            initialRef.current = values;
            refetchUser();
            refetchGlobalStatus();
        } catch (e) {
            console.error(e);
            setStatus("error");
            error = e;
        } finally {
            setSubmitting(false);
        }
}}
    >
      {({ values, handleSubmit, isSubmitting, status, setFieldValue, dirty }) => 
        (
          <>
          {values && values?.suppressions &&
            <Form>
              <h2>Email Unsubscribe Options</h2>
              <Separator />
              <div style={{ marginBottom: 25 }}>
                <b>Email:</b> {decodeURIComponent(emailFromParams)}
              </div>
              {/* Global unsubscribe */}
              <Toggle
                label="Global Unsubscribe (all emails)"
                name="globalUnsubscribe"
                checked={values.globalUnsubscribe || false}
                onChange={(_, checked) => setFieldValue("globalUnsubscribe", checked)}
                onText="Unsubscribed"
                offText="Subscribed"
              />
              <Separator />
              {/* Individual groups */}
              {values.suppressions?.map((group, index) => (
                  <div key={group.id} style={{ marginBottom: 15 }}>
                    <Toggle
                      label={group.name}
                      name={`suppressions[${index}].suppressed`}
                      checked={group.suppressed}
                      onChange={(_, checked) =>
                        setFieldValue(`suppressions[${index}].suppressed`, checked)
                      }
                      onText="Unsubscribed"
                      offText="Subscribed"
                      disabled={values.globalUnsubscribe}
                    />
                    <Text variant="small">
                      {group.description}
                    </Text>
                  </div>
              ))}
              <PrimaryButton
                text="Save"
                type="submit"
                loading={isSubmitting}
                disabled={!dirty || isSubmitting}
                style={{ marginTop: 25, marginBottom: 25 }}
                onClick={handleSubmit}
              />
              <SuccessComponent
                successMessage="Email unsubscribe settings updated."
                showSuccess={status === "success"}
                setShowSuccess={() => {}}
              />
              <ErrorComponent
                errorMessage="Failed to update settings."
                showError={status === "error"}
                setShowError={() => {}}
              />
            </Form>
          }
          </>
      )}
    </Formik>
  );
};

export default MemberEmailUnsubscribeOptions;